import React, { useState, useEffect } from "react";
import {
  CalculatorService,
  type CalculatorLevel,
  type LabWasteSubmissionResponse,
  type FormRow,
  type FormColumn,
} from "../../services/calculatorService";
import { Button } from "../ui/Button";
import { LockSubmitModal, type LockSubmitModalRef } from "./LockSubmitModal";

interface FormData {
  [rowId: string]: { [columnId: string]: string };
}

// interface FormRow {
//   id: string;
//   label: string;
//   is_required?: boolean;
//   isDynamic?: boolean;
//   non_editable_columns?: string[];
// }

// interface FormColumn {
//   id: number;
//   label: string;
//   is_editable: boolean;
//   is_required: boolean;
// }

interface LabWasteSubmissionProps {
  labId: string;
  level: CalculatorLevel;
  formData: FormData;
  formRows: FormRow[];
  formColumns: FormColumn[];
  locked: boolean;
  onSubmissionSuccess: (results: LabWasteSubmissionResponse) => void;
  onSubmissionError: (error: string) => void;
}

// Completion calculation helper functions - temporarily disabled
/*
// Required waste types to track overall completion for lab waste assessments
const REQUIRED_WASTE_TYPES = ['Biohazard', 'General/Landfill', 'Recycling', 'Confidential'];

const isNAValue = (value: unknown): boolean => {
  if (typeof value !== 'string') {
    return false;
  }
  return value.trim().toLowerCase() === 'n/a';
};

const isZeroQuantity = (value: unknown): boolean => {
  if (typeof value !== 'string') {
    return false;
  }
  const trimmed = value.trim();
  // Check for various zero representations
  return trimmed === '0' ||
    trimmed === '0.0' ||
    trimmed === '0.00' ||
    trimmed === '0.000' ||
    parseFloat(trimmed) === 0;
};

const isFieldComplete = (value: unknown): boolean => {
  if (value === null || value === undefined) {
    return false;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.length === 0) {
      return false;
    }
    return true;
  }

  return true;
};

// Check if a row should be considered complete when quantity is 0
const isRowCompleteWithZeroQuantity = (rowData: { [columnId: string]: string }, formColumns: FormColumn[]): boolean => {
  // Find quantity-related columns with broader detection
  const quantityColumns = formColumns.filter(col => {
    const label = col.label.toLowerCase();
    return label.includes('quantity') ||
      label.includes('volume') ||
      label.includes('amount') ||
      label.includes('kg') ||
      label.includes('litre') ||
      label.includes('number') ||  // "Number of Bins"
      label.includes('bins') ||    // "Number of Bins"
      label.includes('weight') ||  // "Weight"
      label.includes('count');     // General count fields
  });

  // If we found quantity columns, check if any has zero value
  if (quantityColumns.length > 0) {
    console.log('🔍 Detected quantity columns:', quantityColumns.map(col => col.label));
    return quantityColumns.some(col => {
      const value = rowData[String(col.id)];
      console.log(`   Checking column "${col.label}" (id: ${col.id}) = "${value}" → isZero: ${isZeroQuantity(value)}`);
      return isZeroQuantity(value);
    });
  }

  // Fallback: check if the first editable column has zero (assuming it's quantity)
  const firstEditableColumn = formColumns.find(col => col.is_editable);
  if (firstEditableColumn) {
    const value = rowData[String(firstEditableColumn.id)];
    return isZeroQuantity(value);
  }

  return false;
};
*/

export const LabWasteSubmission: React.FC<LabWasteSubmissionProps> = ({
  labId,
  level,
  formData,
  formRows,
  formColumns,
  locked,
  onSubmissionSuccess,
  onSubmissionError,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [canSubmit, setCanSubmit] = useState(false);
  const lockSubmitModalRef = React.useRef<LockSubmitModalRef>(null);

  // Synchronize submission ability based on form validation
  useEffect(() => {
    const validateForm = (): boolean => {
      // If no rows, we can't submit
      if (formRows.length === 0) return false;

      // Find "No. of bins" column ID to handle zero-bin logic
      const binsColumn = formColumns.find(
        (c) =>
          c.label.toLowerCase().includes("no. of bins") ||
          c.label.toLowerCase().includes("number of bins"),
      );
      const binsColumnId = binsColumn ? String(binsColumn.id) : null;

      // Identify required columns
      const requiredColumns = formColumns.filter(
        (c) => c.is_required && c.is_editable,
      );

      // Check each row for completeness
      for (const row of formRows) {
        const rowId = String(row.id);
        const rowData = formData[rowId] || {};

        // If bins = 0, this row is considered valid regardless of other fields
        // (The UI logic in the main Calculator component handles clearing/disabling fields)
        if (binsColumnId) {
          const binsValue = rowData[binsColumnId];
          const isZeroBins = binsValue === "0" || binsValue === "0.0";
          if (isZeroBins) {
            continue; // Skip further validation for this row
          }
        }

        // Check required fields for this row
        for (const col of requiredColumns) {
          // Skip if this column is non-editable for this specific row
          if (row.non_editable_columns?.some(id => String(id) === String(col.id))) {
            continue;
          }

          const val = rowData[String(col.id)];
          // Field is invalid if completely empty (Note: 'N/A' is allowed)
          if (!val || (typeof val === "string" && val.trim() === "")) {
            return false;
          }
        }
      }

      // If we got here, all rows are valid
      return true;
    };

    setCanSubmit(validateForm());
    
  }, [formData, formRows, formColumns]);

  const handleSubmit = async () => {
    if (!canSubmit) return;

    try {
      setIsSubmitting(true);
      const response = await CalculatorService.submitLabWasteCalculator(
        labId,
        level,
      );
      onSubmissionSuccess(response);
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to submit lab waste calculator";
      onSubmissionError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (locked) {
    return null;
  }

  return (
    <div className="space-y-3 sm:space-y-4 w-full">
      <div className="flex flex-col lg:flex-row gap-2 sm:gap-3 sm:justify-end">
        <Button
          variant="primary"
          onClick={() => {
            lockSubmitModalRef.current?.showModal();
          }}
          disabled={!canSubmit || isSubmitting}
        >
          {isSubmitting ? (
            <>
              <span className="loading loading-spinner loading-sm mr-2"></span>
              <span className="text-xs sm:text-sm">Submitting...</span>
            </>
          ) : (
            <>
              <span className="hidden sm:inline">
                Submit Lab Waste Assessment
              </span>
              <span className="sm:hidden text-xs">Submit Assessment</span>
            </>
          )}
        </Button>
      </div>

      {/* Confirmation Modal */}
      <LockSubmitModal
        ref={lockSubmitModalRef}
        onConfirm={handleSubmit}
        onCancel={() => {
          /* no-op */
        }}
        loading={isSubmitting}
      />
    </div>
  );
};

export default LabWasteSubmission;
